import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import SarapeDivider from "@/components/SarapeDivider";
import NewsletterSignup from "@/components/NewsletterSignup";

function fmtDate(iso) {
  try {
    return format(new Date(iso), "MMMM d, yyyy");
  } catch {
    return "";
  }
}

const MD = {
  h2: (props) => <h2 className="text-2xl font-normal text-charcoal-800 mt-10 mb-3" {...props} />,
  h3: (props) => <h3 className="text-xl font-medium text-charcoal-800 mt-8 mb-2" {...props} />,
  p: (props) => <p className="text-charcoal-700 leading-relaxed mb-4 text-lg" {...props} />,
  ul: (props) => <ul className="list-disc pl-6 space-y-1.5 mb-5 text-charcoal-700 text-lg" {...props} />,
  ol: (props) => <ol className="list-decimal pl-6 space-y-1.5 mb-5 text-charcoal-700 text-lg" {...props} />,
  li: (props) => <li className="leading-relaxed" {...props} />,
  a: (props) => <a className="text-terracotta underline underline-offset-2 hover:text-terracotta-hover" {...props} />,
  strong: (props) => <strong className="font-semibold text-charcoal-800" {...props} />,
  blockquote: (props) => <blockquote className="border-l-4 border-terracotta pl-5 italic text-charcoal-600 my-6" {...props} />,
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined); // undefined=loading, null=not found

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/blog/posts/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setPost(null));
  }, [slug]);

  if (post === undefined) {
    return <div className="mx-auto max-w-3xl px-6 py-32 text-charcoal-500">Loading…</div>;
  }

  if (post === null) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-32 text-center" data-testid="blog-not-found">
        <h1 className="text-3xl font-normal text-charcoal-800">Article not found</h1>
        <p className="mt-3 text-charcoal-600">This post may have moved or is no longer available.</p>
        <Link to="/blog" className="mt-6 inline-block rounded-full bg-terracotta px-6 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors">
          Back to all articles
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="page-blog-post">
      <Seo
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt}
        image={post.cover_image}
        type="article"
        path={`/blog/${post.slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.meta_description || post.excerpt,
          image: post.cover_image || undefined,
          datePublished: post.published_at,
          dateModified: post.updated_at || post.published_at,
          author: { "@type": "Organization", name: post.author || "Hola Movers" },
          publisher: { "@type": "Organization", name: "Hola Movers" },
        }}
      />
      <article>
        {/* Header */}
        <header className="bg-sand-100">
          <div className="mx-auto max-w-3xl px-6 lg:px-12 pt-16 lg:pt-24 pb-10">
            <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-terracotta transition-colors mb-8">
              <ArrowLeft size={16} /> All articles
            </Link>
            <div className="flex flex-wrap gap-2 mb-4">
              {(post.tags || []).map((t) => (
                <span key={t} className="rounded-full bg-sage-light px-3 py-1 text-xs font-medium text-sage-700">{t}</span>
              ))}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-charcoal-900 leading-[1.1]">{post.title}</h1>
            <p className="mt-5 text-sm text-charcoal-500">By {post.author} · {fmtDate(post.published_at)}</p>
          </div>
        </header>
        <SarapeDivider />

        {post.cover_image && (
          <div className="mx-auto max-w-4xl px-6 lg:px-12 -mt-0 pt-10">
            <img src={post.cover_image} alt={post.title} className="w-full h-[280px] lg:h-[440px] object-cover rounded-3xl border border-sand-300 shadow-[0_8px_32px_rgba(42,38,36,0.06)]" />
          </div>
        )}

        {/* Body */}
        <div className="mx-auto max-w-3xl px-6 lg:px-12 py-12 lg:py-16">
          <ReactMarkdown components={MD}>{post.body}</ReactMarkdown>
        </div>
      </article>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 pb-8">
        <div className="rounded-3xl bg-sage-light p-10 lg:p-16 text-center">
          <h2 className="text-3xl font-normal text-charcoal-800">Ready to plan your move?</h2>
          <p className="mt-3 text-charcoal-600 max-w-xl mx-auto">Book a 45-minute intro call, or send us a message — whatever feels right for you.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/book" data-testid="blogpost-book-cta" className="rounded-full bg-terracotta px-8 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors">Book an intro call</Link>
            <Link to="/contact" data-testid="blogpost-contact-cta" className="rounded-full border border-charcoal-300 px-8 py-3 text-charcoal-800 font-medium hover:bg-sand-200 transition-colors">Send a message</Link>
          </div>
        </div>
      </section>

      <NewsletterSignup source="blog-post" />
    </div>
  );
}
